import type { SearchableItem } from '../types';

/**
 * Remote search result from API
 */
export interface RemoteSearchResult {
  entityType: string;
  entityId: string;
  displayName: string;
  formName: string;
}

/**
 * Remote search provider function type
 */
export type RemoteSearchProvider = (query: string) => Promise<RemoteSearchResult[]>;

/**
 * SearchManager - Handles search functionality across navigation items
 * Supports both local (navigation) and remote (API) search
 */
export class SearchManager {
  private items: SearchableItem[] = [];
  private input: HTMLInputElement | null = null;
  private results: HTMLElement | null = null;
  private remoteSearchProvider: RemoteSearchProvider | null = null;

  constructor(
    private readonly inputId: string,
    private readonly resultsId: string,
    private readonly onFormSelect: (formName: string) => void
  ) {}

  /**
   * Initialize search functionality
   */
  public init(): void {
    this.input = document.getElementById(this.inputId) as HTMLInputElement | null;
    this.results = document.getElementById(this.resultsId);

    if (!this.input || !this.results) return;

    this.input.addEventListener("input", () => this.handleInput());
    this.input.addEventListener("keydown", (e) => this.handleKeydown(e));
  }

  /**
   * Set the searchable items
   */
  public setItems(items: SearchableItem[]): void {
    this.items = items;
  }

  /**
   * Set a remote search provider for API-based search
   * This is called by tenant-specific code to add remote search capabilities
   */
  public setRemoteSearchProvider(provider: RemoteSearchProvider): void {
    this.remoteSearchProvider = provider;
  }

  /**
   * Handle input changes - hide results when input changes
   */
  private handleInput(): void {
    if (!this.input || !this.results) return;
    
    // Hide results when user is still typing - they need to press Enter
    if (this.input.value.trim().length === 0) {
      this.results.classList.add("hidden");
      this.results.innerHTML = "";
    }
  }

  /**
   * Handle keyboard events
   */
  private handleKeydown(e: KeyboardEvent): void {
    if (!this.input || !this.results) return;
    
    if (e.key === "Escape") {
      this.input.value = "";
      this.results.classList.add("hidden");
    } else if (e.key === "Enter") {
      e.preventDefault();
      const query = this.input.value.trim();
      
      if (query.length < 2) {
        this.results.innerHTML = `<div class="px-3 py-2 text-sm opacity-60">Enter at least 2 characters</div>`;
        this.results.classList.remove("hidden");
        return;
      }
      
      // Show local results first
      this.renderLocalResults(query);
      
      // Then fetch remote results if provider exists
      if (this.remoteSearchProvider) {
        this.fetchAndRenderRemoteResults(query);
      }
    }
  }

  /**
   * Render local search results (navigation items)
   */
  private renderLocalResults(query: string): void {
    if (!this.results) return;

    const q = query.toLowerCase();
    
    if (!q) {
      this.results.classList.add("hidden");
      this.results.innerHTML = "";
      return;
    }

    const matches = this.items.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.form.toLowerCase().includes(q) ||
        item.group.toLowerCase().includes(q)
    );

    if (!matches.length) {
      // Don't show "no matches" yet - remote results may come
      if (!this.remoteSearchProvider) {
        this.results.innerHTML = `<div class="px-3 py-2 text-sm opacity-60">No matches</div>`;
        this.results.classList.remove("hidden");
      } else {
        // Show loading indicator while waiting for remote results
        this.results.innerHTML = `
          <div class="px-3 py-2 text-sm flex items-center gap-2">
            <span class="loading loading-spinner loading-xs"></span>
            Searching...
          </div>`;
        this.results.classList.remove("hidden");
      }
      return;
    }

    this.results.innerHTML = `
      <div class="px-3 py-1 text-xs font-semibold opacity-50">Navigation</div>
      ${matches.map(
        (m) => `
        <a href="#" data-search-form="${m.form}" class="flex items-center gap-2 px-3 py-2 hover:bg-base-300 rounded-lg cursor-pointer text-sm">
          <i class="fas fa-file-alt text-base-content/50 w-5 text-center flex-shrink-0"></i>
          <span class="font-semibold flex-1 truncate">${m.label}</span>
          <span class="text-xs opacity-70 flex-shrink-0">(${m.group})</span>
        </a>`
      ).join("")}
    `;
    
    this.results.classList.remove("hidden");
    this.wireUpResultClickHandlers();
  }

  /**
   * Fetch and render remote search results
   */
  private async fetchAndRenderRemoteResults(query: string): Promise<void> {
    if (!this.results || !this.remoteSearchProvider || !this.input) return;
    
    // Check query hasn't changed during debounce
    if (this.input.value.trim() !== query) return;
    
    try {
      const remoteResults = await this.remoteSearchProvider(query);
      
      // Check query still matches
      if (this.input.value.trim() !== query) return;
      
      // Get current local results
      const q = query.toLowerCase();
      const localMatches = this.items.filter(
        (item) =>
          item.label.toLowerCase().includes(q) ||
          item.form.toLowerCase().includes(q) ||
          item.group.toLowerCase().includes(q)
      );
      
      // Build combined HTML
      let html = '';
      
      // Local results section
      if (localMatches.length > 0) {
        html += `<div class="px-3 py-1 text-xs font-semibold opacity-50">Navigation</div>`;
        html += localMatches.map(
          (m) => `
          <a href="#" data-search-form="${m.form}" class="flex items-center gap-2 px-3 py-2 hover:bg-base-300 rounded-lg cursor-pointer text-sm">
            <i class="fas fa-file-alt text-base-content/50 w-5 text-center flex-shrink-0"></i>
            <span class="font-semibold flex-1 truncate">${m.label}</span>
            <span class="text-xs opacity-70 flex-shrink-0">(${m.group})</span>
          </a>`
        ).join("");
      }
      
      // Remote results section
      if (remoteResults.length > 0) {
        html += `<div class="px-3 py-1 text-xs font-semibold opacity-50 mt-2">Data</div>`;
        html += remoteResults.map((r) => {
          const icon = this.getEntityIcon(r.entityType);
          const badgeClass = this.getEntityBadgeClass(r.entityType);
          return `
            <a href="#" data-search-form="${r.formName}" data-entity-id="${r.entityId}" class="flex items-center gap-2 px-3 py-2 hover:bg-base-300 rounded-lg cursor-pointer text-sm">
              <i class="${icon} text-base-content/50 w-5 text-center flex-shrink-0"></i>
              <span class="font-semibold flex-1 truncate">${r.displayName}</span>
              <span class="badge badge-xs ${badgeClass} flex-shrink-0">${r.entityType}</span>
            </a>`;
        }).join("");
      }
      
      // No results at all
      if (!localMatches.length && !remoteResults.length) {
        html = `<div class="px-3 py-2 text-sm opacity-60">No matches found</div>`;
      }
      
      this.results.innerHTML = html;
      this.results.classList.remove("hidden");
      this.wireUpResultClickHandlers();
      
    } catch (error) {
      console.error('Remote search failed:', error);
      // Keep showing local results if remote fails
    }
  }

  /**
   * Get icon class for entity type
   */
  private getEntityIcon(entityType: string): string {
    const icons: Record<string, string> = {
      'Footballer': 'fas fa-futbol',
      'Team': 'fas fa-shield-alt',
      'Competition': 'fas fa-trophy',
      'Stadium': 'fas fa-building',
      'Agent': 'fas fa-user-tie',
      'Contact': 'fas fa-address-book'
    };
    return icons[entityType] || 'fas fa-database';
  }

  /**
   * Get badge class for entity type
   */
  private getEntityBadgeClass(entityType: string): string {
    const badges: Record<string, string> = {
      'Footballer': 'badge-primary',
      'Team': 'badge-secondary',
      'Competition': 'badge-accent',
      'Stadium': 'badge-info',
      'Agent': 'badge-warning',
      'Contact': 'badge-success'
    };
    return badges[entityType] || 'badge-ghost';
  }

  /**
   * Wire up click handlers for all search result items
   */
  private wireUpResultClickHandlers(): void {
    if (!this.results) return;
    
    this.results.querySelectorAll("[data-search-form]").forEach((a) => {
      a.addEventListener("click", (e) => {
        e.preventDefault();
        const el = e.currentTarget as HTMLElement;
        const formName = el.getAttribute("data-search-form")!;
        const entityId = el.getAttribute("data-entity-id");
        
        if (this.input) this.input.value = "";
        this.results!.classList.add("hidden");
        
        // Navigate to form, optionally with entity ID
        if (entityId) {
          this.onFormSelect(`${formName}?id=${entityId}`);
        } else {
          this.onFormSelect(formName);
        }
      });
    });
  }
}
